import React from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import styled from '@emotion/styled';

import { NavLinkButton, Breadcrumb, Col, Row } from '@shared/components/bootstrap';
import { FEATURE_FLAG } from '@shared/config/feature_flag';
import { UserRole } from '@shared/types/user_role';

import { Map as CheckinsMap } from '@admin/components/checkins/map';
import { Label } from '@admin/components/helpers/label';
import { Panel } from '@admin/components/helpers/panel';
import { Roles } from '@admin/components/helpers/roles';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { Formatter } from '@admin/components/phone/formatter';
import { GenerateTestPassword } from '@admin/components/qa_automation/generate_test_password';
import { SendGridStatusButton } from '@admin/components/sendgrid/send_grid_status_button';
import { CreateTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { ROLES } from '@admin/config/roles';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useUsersDetailsQuery, History__Subject, UserNotificationPreferenceEnum } from '@admin/schema';
import { Ticket__TicketableEnum } from '@admin/schema';

import { Activity } from './activity';
import { Avatar } from './avatar';
import { ExternalGroup } from './external_group';
import { ForceLeaveCallCenter } from './force_leave_call_center';
import { GeneratePasswordResetLinkButton, AccessWorkforceAccountButton } from './generate_password_reset_link_button';
import { Role } from './role';
import { UserSkillsPanel } from './user_skills_panel';

export const PREFERENCE_TO_LABEL = {
  [UserNotificationPreferenceEnum.PersonalEmail]: 'Personal email',
  [UserNotificationPreferenceEnum.PersonalPhone]: 'SMS',
  [UserNotificationPreferenceEnum.PersonalEmailAndPhone]: 'Personal email & SMS',
  [UserNotificationPreferenceEnum.WorkEmailOnly]: 'Work email only',
};

const Labels = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
`;

export const Details: React.FC = () => {
  useTitle('User');
  const { id } = useParams<{ id: string }>();
  const { data } = useUsersDetailsQuery({ client, variables: { id } });
  const user = data?.user;

  const allowedToGenerateTestPassword = ROLES.has(UserRole.Admin);
  const showPasswordResetLink =
    FEATURE_FLAG.admin_user_password_reset_link !== false && !!user?.policy.canGeneratePasswordResetLink;

  return (
    <>
      <Roles show={[UserRole.Admin, UserRole.Manager, UserRole.EnterpriseManager, UserRole.L2Agent]}>
        <div className="pull-right">
          <CreateTicket ticketable={{ id, type: Ticket__TicketableEnum.User, displayName: user?.name }} />
        </div>
      </Roles>

      <div className="page-header text-overflow">
        <Navigation>
          <Breadcrumb>
            <Breadcrumb.Item>
              <NavLink to="/users">Users</NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{user?.name ?? '-'}</Breadcrumb.Item>
          </Breadcrumb>
        </Navigation>
      </div>

      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <Row>
            <Col md={3}>{user && <Avatar user={user} />}</Col>
            <Col md={9}>
              <p>
                <strong>Teal ID:</strong> {user?.id ?? '-'}
              </p>
              <p>
                <strong>Workday User ID:</strong> {user?.workdayUserID ?? '-'}
              </p>
              <p>
                <strong>Name:</strong> {user?.name ?? '-'}
              </p>
              <p>
                <strong>Email:</strong> {user ? <a href={`mailto:${user.email}`}>{user.email}</a> : <span>-</span>}
              </p>
              {user?.personalEmail && (
                <p>
                  <strong>Personal Email:</strong> {<a href={`mailto:${user.personalEmail}`}>{user.personalEmail}</a>}
                </p>
              )}
              <p>
                <strong>Notification Preferences:</strong>{' '}
                {user?.notificationPreference ? PREFERENCE_TO_LABEL[user.notificationPreference] : '-'}
              </p>
              <p>
                <strong>Referral Code:</strong> {user?.promoCode.value ?? '-'}
              </p>
              <p>
                <strong>Phone:</strong> {user?.phone ? <Formatter number={user.phone} /> : <span>-</span>}
              </p>
              <p>
                <strong>Roles:</strong>{' '}
                {user ? (
                  <Labels>
                    {user.roles.map((role, index) => (
                      <Role key={index} role={role} />
                    ))}
                  </Labels>
                ) : (
                  <span>-</span>
                )}
              </p>
              <p>
                <strong>Team:</strong> {user?.team ? <Label kind="default">{user.team.name}</Label> : <span>-</span>}
              </p>
              {user?.externalGroup && (
                <p>
                  <strong>External Group:</strong> <ExternalGroup externalGroup={user.externalGroup} />
                </p>
              )}
              <p>
                <strong>Owned Teams:</strong>{' '}
                {user ? (
                  <Labels>
                    {user.ownedTeams.map((team) => (
                      <Label key={team.id} kind="default">
                        {team.name}
                      </Label>
                    ))}
                  </Labels>
                ) : (
                  <span>-</span>
                )}
              </p>
              <p>
                <strong>Logins:</strong> <span>{user?.signInCount ?? '-'}</span>
              </p>
              <p>{user && <ForceLeaveCallCenter user={user} />}</p>
              {allowedToGenerateTestPassword && (
                <p>
                  <GenerateTestPassword resourceID={id} resourceType="User" />
                </p>
              )}
            </Col>
          </Row>
        </Panel.Body>
        <Panel.Footer align="right">
          <FooterActions>
            <SendGridStatusButton email={user?.email} />

            {showPasswordResetLink && (
              <GeneratePasswordResetLinkButton userId={id} displayName={user?.name ?? 'this user'} />
            )}
            {user?.active && <AccessWorkforceAccountButton userID={id} displayName={user?.name ?? 'this user'} />}
            {user?.policy.canUpdate && (
              <NavLinkButton kind="primary" to={`/users/${id}/edit`}>
                Edit
              </NavLinkButton>
            )}
          </FooterActions>
        </Panel.Footer>
      </Panel>

      {user?.policy.canViewSkills && <UserSkillsPanel skills={user.skills} skillTrainings={user.skillTrainings} />}

      <CheckinsMap id={id} type="users" />
      <Activity id={id} />
      <HistoriesPanel type={History__Subject.User} id={id} />
    </>
  );
};
